import React, { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon, PlusIcon } from '@heroicons/react/24/outline';

/**
 * Speaker Selector Component
 * 
 * Dropdown for selecting and managing speakers
 */
const SpeakerSelector = ({
  value,
  speakers = [],
  onChange,
  onAddNew,
  className = '',
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddingNew, setIsAddingNew] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  const speakerInitials = (name) =>
    (name || '?')
      .split(/\s+/)
      .map((p) => p[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase();

  /**
   * Filter speakers by search query
   */
  const filteredSpeakers = speakers.filter((s) =>
    s.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  /**
   * Handle speaker selection
   */
  const handleSelect = (speaker) => {
    onChange(speaker);
    setIsOpen(false);
    setSearchQuery('');
    setIsAddingNew(false);
  };

  /**
   * Handle add new speaker
   */
  const handleAddNew = () => {
    if (!searchQuery.trim()) return;

    onAddNew?.(searchQuery.trim());
    onChange(searchQuery.trim());
    setSearchQuery('');
    setIsOpen(false);
    setIsAddingNew(false);
  };

  /**
   * Handle click outside
   */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchQuery('');
        setIsAddingNew(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /**
   * Focus input when adding new
   */
  useEffect(() => {
    if (isAddingNew && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isAddingNew]);

  return (
    <div ref={dropdownRef} className={`te-speaker-select ${className}`}>
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`te-speaker-chip${disabled ? ' is-disabled' : ''}${isOpen ? ' is-open' : ''}`}
        title={value || 'Select speaker'}
      >
        <span className="te-speaker-chip-avatar">{speakerInitials(value || '?')}</span>
        <span className="te-speaker-chip-name">{value || 'Select speaker'}</span>
        <ChevronDownIcon
          className={`h-3.5 w-3.5 te-speaker-chip-chev${isOpen ? ' is-open' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="te-speaker-dropdown">
          <div className="te-speaker-dropdown-search">
            <input
              type="text"
              placeholder="Search or add speaker..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && searchQuery.trim()) {
                  handleAddNew();
                }
              }}
              className="te-speaker-dropdown-input"
              autoFocus
            />
          </div>

          <div className="te-speaker-dropdown-list">
            {filteredSpeakers.length > 0 ? (
              filteredSpeakers.map((speaker) => (
                <button
                  key={speaker.name}
                  onClick={() => handleSelect(speaker.name)}
                  className="te-speaker-dropdown-row"
                >
                  <span className="te-speaker-dropdown-name">{speaker.name}</span>
                  <span className="te-speaker-dropdown-count">
                    {speaker.count}
                  </span>
                </button>
              ))
            ) : searchQuery.trim() ? (
              <button
                onClick={handleAddNew}
                className="te-speaker-dropdown-row te-speaker-dropdown-row-add"
              >
                <PlusIcon className="h-3.5 w-3.5" />
                <span>Add "{searchQuery}"</span>
              </button>
            ) : (
              <p className="te-speaker-dropdown-empty">No speakers found</p>
            )}
          </div>

          {!searchQuery && (
            <div className="te-speaker-dropdown-foot">
              <button
                onClick={() => setIsAddingNew(true)}
                className="te-btn te-btn-ghost"
                style={{ width: '100%', justifyContent: 'center' }}
              >
                <PlusIcon className="h-3.5 w-3.5" />
                Add new speaker
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SpeakerSelector;


